// app/helpers/interactions/context.js

// ----------------------------------------------------------------------------
// Build the context object consumed by all interaction handlers
// ----------------------------------------------------------------------------
export function buildContext(args) {
  const {
    // refs
    canvasRef,

    // immutable props
    activeTool,
    isPanMode,
    scale,
    unit,
    comments,

    // sizes/transforms
    canvasSize,
    displaySize,
    zoom,
    imageTransforms,

    // callbacks
    setActiveTool,
    onMeasurementAdd,
    onShowCommentInput,
    onShowCommentThread,
    onSelectComment,
    onShowCommentTooltip,
    onZoomChange,
    addToHistory,

    // state bundle
    state,
    updateMeasurementRunTime,
    setSelectedObjectId,
    onPolygonEditStart,
  } = args;

  const {
    // drawing / collections
    measurements,
    setMeasurements,
    textAnnotations,
    setTextAnnotations,
    guidelines,
    setGuidelines,
    polygons,
    setPolygons,
    dots,
    setDots,
    selectedDot,
    setSelectedDot,
    isDraggingDot,
    setIsDraggingDot,
    draggedDotId,
    setDraggedDotId,
    dotDragOffset,
    setDotDragOffset,

    arrows,
    setArrows,
    selectedArrowId,
    setSelectedArrowId,
    isDraggingArrow,
    setIsDraggingArrow,
    draggedArrowId,
    setDraggedArrowId,
    arrowDragMode,
    setArrowDragMode,

    // progressive aid
    currentPoints,
    setCurrentPoints,
    isDrawing,
    setIsDrawing,

    // selection
    selectedMeasurement,
    setSelectedMeasurement,
    selectedText,
    setSelectedText,
    selectedGuideline,
    setSelectedGuideline,
    selectedPolygon,
    setSelectedPolygon,

    // hovers
    hoveredPoint,
    setHoveredPoint,

    // panning / zooming
    imageOffset,
    setImageOffset,
    isPanning,
    setIsPanning,
    lastPanPoint,
    setLastPanPoint,
    touchStartDistance,
    setTouchStartDistance,
    touchStartZoom,
    setTouchStartZoom,

    // line dragging
    isDragging,
    setIsDragging,
    dragPoint,
    setDragPoint,
    isDraggingLine,
    setIsDraggingLine,
    draggedLineId,
    setDraggedLineId,

    // generic drag offset
    dragOffset,
    setDragOffset,

    // scale ruler
    scalePosition,
    setScalePosition,
    scaleLength,
    setScaleLength,
    scaleAngle,
    setScaleAngle,
    isDraggingScale,
    setIsDraggingScale,
    isDraggingScaleEnd,
    setIsDraggingScaleEnd,

    // text drag/resize/rotate
    isDraggingText,
    setIsDraggingText,
    draggedTextId,
    setDraggedTextId,
    isResizingText,
    setIsResizingText,
    textResizeHandle,
    setTextResizeHandle,
    isRotatingText,
    setIsRotatingText,

    // guidelines drag
    isDraggingGuideline,
    setIsDraggingGuideline,
    draggedGuidelineId,
    setDraggedGuidelineId,

    // polygons drag
    isDraggingPolygon,
    setIsDraggingPolygon,
    draggedPolygonId,
    setDraggedPolygonId,
    draggedPointIndex,
    setDraggedPointIndex,

    // canvas resize UI
    isResizingCanvas,
    setIsResizingCanvas,
    resizeStart,
    setResizeStart,
    resizeStartSize,
    setResizeStartSize,
    isOpenPolygonMode,
    setIsOpenPolygonMode,

    // reupdate measuremnt array when draw completed or changed,
  } = state;

  // convenience
  const isLinearTool = activeTool === "linear" || activeTool === "line";

  // Map DOM/touch events to image-space coords
  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    const dsW = Math.max(1, Number(displaySize?.width ?? canvas.width ?? 1));
    const dsH = Math.max(1, Number(displaySize?.height ?? canvas.height ?? 1));
    const csW = Math.max(1, Number(canvasSize?.width ?? canvas.width ?? 1));
    const csH = Math.max(1, Number(canvasSize?.height ?? canvas.height ?? 1));

    const scaleX = csW / dsW;
    const scaleY = csH / dsH;

    let clientX, clientY;
    if ("touches" in event) {
      clientX =
        event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX ?? 0;
      clientY =
        event.touches[0]?.clientY ?? event.changedTouches[0]?.clientY ?? 0;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }

    let x = (clientX - rect.left) * scaleX;
    let y = (clientY - rect.top) * scaleY;

    const rotation = Number(imageTransforms?.rotation ?? 0);
    const pos = imageTransforms?.position ?? { x: 0, y: 0 };

    const centerX = csW / 2;
    const centerY = csH / 2;
    x -= centerX;
    y -= centerY;

    const rot = (-rotation * Math.PI) / 180;
    const rx = x * Math.cos(rot) - y * Math.sin(rot);
    const ry = x * Math.sin(rot) + y * Math.cos(rot);
    x = rx - pos.x;
    y = ry - pos.y;

    const z = Math.max(0.0001, Number(zoom ?? 1));
    x = (x - (imageOffset?.x ?? 0)) / z;
    y = (y - (imageOffset?.y ?? 0)) / z;

    x += centerX;
    y += centerY;
    return { x, y };
  };

  const addGuideline = (type) => {
    const existing = guidelines.filter((g) => g.type === type);
    let position;
    if (existing.length === 0) {
      position =
        type === "vertical" ? canvasSize.width / 2 : canvasSize.height / 2;
    } else {
      const last = existing[existing.length - 1];
      position = last.position + 50;
    }
    const guideline = { id: Date.now().toString(), type, position };
    setGuidelines((prev) => [...prev, guideline]);
    addToHistory();
  };

  return {
    // props
    fabricCanvasRef: args.fabricCanvasRef,
    canvasRef,
    activeTool,
    isPanMode,
    scale,
    unit,
    comments,
    canvasSize,
    displaySize,
    zoom,
    imageTransforms,
    setActiveTool,
    onMeasurementAdd,
    onShowCommentInput,
    onShowCommentThread,
    onSelectComment,
    onShowCommentTooltip,
    onZoomChange,
    addToHistory,

    // state
    measurements,
    setMeasurements,
    textAnnotations,
    setTextAnnotations,
    guidelines,
    setGuidelines,
    polygons,
    setPolygons,
    dots,
    setDots,
    selectedDot,
    setSelectedDot,
    isDraggingDot,
    setIsDraggingDot,
    draggedDotId,
    setDraggedDotId,
    dotDragOffset,
    setDotDragOffset,
    currentPoints,
    setCurrentPoints,
    isDrawing,
    setIsDrawing,
    selectedMeasurement,
    setSelectedMeasurement,
    selectedText,
    setSelectedText,
    selectedGuideline,
    setSelectedGuideline,
    selectedPolygon,
    setSelectedPolygon,
    hoveredPoint,
    setHoveredPoint,
    imageOffset,
    setImageOffset,
    isPanning,
    setIsPanning,
    lastPanPoint,
    setLastPanPoint,
    touchStartDistance,
    setTouchStartDistance,
    touchStartZoom,
    setTouchStartZoom,
    isDragging,
    setIsDragging,
    dragPoint,
    setDragPoint,
    isDraggingLine,
    setIsDraggingLine,
    draggedLineId,
    setDraggedLineId,
    dragOffset,
    setDragOffset,
    scalePosition,
    setScalePosition,
    scaleLength,
    setScaleLength,
    scaleAngle,
    setScaleAngle,
    isDraggingScale,
    setIsDraggingScale,
    isDraggingScaleEnd,
    setIsDraggingScaleEnd,
    isDraggingText,
    setIsDraggingText,
    draggedTextId,
    setDraggedTextId,
    isResizingText,
    setIsResizingText,
    textResizeHandle,
    setTextResizeHandle,
    isRotatingText,
    setIsRotatingText,
    isDraggingGuideline,
    setIsDraggingGuideline,
    draggedGuidelineId,
    setDraggedGuidelineId,
    isDraggingPolygon,
    setIsDraggingPolygon,
    draggedPolygonId,
    setDraggedPolygonId,
    draggedPointIndex,
    setDraggedPointIndex,
    isResizingCanvas,
    setIsResizingCanvas,
    resizeStart,
    setResizeStart,
    resizeStartSize,
    setResizeStartSize,
    setSelectedObjectId,

    // helpers
    isLinearTool,
    getCanvasPoint,
    addGuideline,
    updateMeasurementRunTime,
    isOpenPolygonMode,
    setIsOpenPolygonMode,
    onPolygonEditStart,

    arrows,
    setArrows,
    selectedArrowId,
    setSelectedArrowId,
    isDraggingArrow,
    setIsDraggingArrow,
    draggedArrowId,
    setDraggedArrowId,
    arrowDragMode,
    setArrowDragMode,
  };
}

// ----------------------------------------------------------------------------
// Calibration helpers (px -> real units) used by handlers
// // ----------------------------------------------------------------------------

export const distUnits = (a, b, { scale, scaleLength } = {}) => {
  const dx = (b?.x ?? 0) - (a?.x ?? 0);
  const dy = (b?.y ?? 0) - (a?.y ?? 0);
  const dpx = Math.hypot(dx, dy); // measureLineDistance (px)

  const s = Number(scale) || 0; // "unit" value (could be '10' for px, or 20 for mm)
  const ref = Number(scaleLength) || 0; // referenceLineDistance (px)

  return s > 0 && ref > 0 ? (dpx / ref) * s : dpx;
};
